import type { KsefInvoice, KsefInvoiceType } from './types';

/**
 * KSeF invoice types whose synced detail we only partially reconstruct today: the parser bails on
 * VAT RR and its correction (a different schema root — `FA_RR`, no `Fa`/`FaWiersz`), never reads the
 * `Zamowienie`/`Rozliczenie` blocks that carry prepayment/settlement lines, and hasn't been verified
 * against PEF. Standard VAT, simplified, and ordinary corrections are handled faithfully and are
 * intentionally absent — as is VAT marża, which has no distinct type on the wire anyway (it is a
 * standard-VAT document flagged by `PMarzy`) and is now rendered in full.
 */
const LIMITED_DETAIL_TYPES: ReadonlySet<KsefInvoiceType> = new Set([
  'VAT_RR',
  'VAT_SP_CORRECTION',
  'PREPAYMENT',
  'SETTLEMENT',
  'PREPAYMENT_CORRECTION',
  'SETTLEMENT_CORRECTION',
  'VAT_PEF',
  'VAT_PEF_SP',
  'CORRECTION_PEF',
]);

/**
 * Whether the invoice is a shape whose synced detail we can't yet render faithfully — detectable from
 * list metadata alone, no parsed body required. VAT RR is also caught by its `FA_RR` form code, so it
 * is still flagged if the type ever arrives unmapped.
 *
 * Margin-scheme invoices used to be flagged here. They no longer are: the parser reads the `PMarzy`
 * procedure and the gross-priced lines (`P_9B`/`P_11A`), and the list and detail views render both, so
 * there is nothing missing to warn about.
 */
export function hasLimitedDetail(inv: Pick<KsefInvoice, 'invoiceType' | 'formCode'>): boolean {
  if (inv.formCode === 'FA_RR') return true;
  return inv.invoiceType != null && LIMITED_DETAIL_TYPES.has(inv.invoiceType);
}
